import { useState, useMemo, useEffect } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { apiClient } from '../utils/api'
import { LogOut, Settings2, User, Video, GitCommitHorizontal, BarChart3, Award, Target, Eye, Search, Building2, MessageSquare, Bell, Clock, Upload, UserPlus, Bookmark, ChevronRight, Mail, FileText, ExternalLink, Share2 } from 'lucide-react'
import PlayerDashboardProfile from './PlayerDashboardProfile'
import PlayerMatchClips from './PlayerMatchClips'
import PlayerCareerTimeline from './PlayerCareerTimeline'
import PlayerStatistics from './PlayerStatistics'
import PlayerAchievements from './PlayerAchievements'
import PlayerMessages from './PlayerMessages'
import PlayerTrialInvitations from './PlayerTrialInvitations'
import PlayerDocuments from './PlayerDocuments'
import PlayerNotifications from './PlayerNotifications'
import PlayerSettings from './PlayerSettings'
import PublicPlayerProfile from './PublicPlayerProfile'
import AffiliateDashboard from './AffiliateDashboard'
import { useVideoStore } from '../stores/useVideoStore'
import { useAchievementStore } from '../stores/useAchievementStore'
import { useMessageStore } from '../stores/useMessageStore'

type TabType = 'dashboard' | 'profile' | 'clips' | 'statistics' | 'timeline' | 'achievements' | 'messages' | 'trials' | 'documents' | 'notifications' | 'settings' | 'affiliates'

const TABS: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <User size={16} /> },
  { id: 'profile', label: 'My Profile', icon: <User size={16} /> },
  { id: 'clips', label: 'My Match Clips', icon: <Video size={16} /> },
  { id: 'statistics', label: 'Statistics', icon: <BarChart3 size={16} /> },
  { id: 'timeline', label: 'Career Timeline', icon: <GitCommitHorizontal size={16} /> },
  { id: 'achievements', label: 'Achievements', icon: <Award size={16} /> },
  { id: 'messages', label: 'Messages', icon: <MessageSquare size={16} /> },
  { id: 'trials', label: 'Trial Invitations', icon: <Mail size={16} /> },
  { id: 'documents', label: 'Documents', icon: <FileText size={16} /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell size={16} /> },
  { id: 'settings', label: 'Settings', icon: <Settings2 size={16} /> },
  { id: 'affiliates', label: 'Affiliates', icon: <Target size={16} /> },
]

function PlayerDashboard() {
  const { user, logout } = useAuthStore()
  const { players } = usePlayerStore()
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [notifications, setNotifications] = useState<any[]>([])
  const [showPreview, setShowPreview] = useState(false)

  const { videos, fetchVideos } = useVideoStore()
  const { achievements, fetchAchievements } = useAchievementStore()
  const { unreadCount, fetchUnreadCount } = useMessageStore()

  const currentPlayer = useMemo(() => {
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [user, players])

  useEffect(() => {
    if (currentPlayer?.id) {
      apiClient.getPlayerNotifications(currentPlayer.id)
        .then(setNotifications)
        .catch(() => setNotifications([]))
    }
  }, [currentPlayer?.id])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchVideos(currentPlayer.id)
      fetchAchievements(currentPlayer.id)
      fetchUnreadCount(user!.id, 'player', currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchVideos, fetchAchievements, fetchUnreadCount, user])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchVideos(currentPlayer.id)
      fetchAchievements(currentPlayer.id)
      fetchUnreadCount(user!.id, 'player', currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchVideos, fetchAchievements, fetchUnreadCount, user])

  const [shareCopied, setShareCopied] = useState(false)
  const handleShare = () => {
    if (!currentPlayer) return
    const url = new URL(window.location.origin + window.location.pathname)
    url.hash = `#/player-profile/${currentPlayer.id}`
    navigator.clipboard.writeText(url.toString())
    setShareCopied(true)
    setTimeout(() => setShareCopied(false), 2000)
  }

  const profileStrength = useMemo(() => {
    if (!currentPlayer) return 0
    
    // 1. Metadata Fields - worth up to 50 pts
    const fields = [
      currentPlayer.name,
      currentPlayer.age,
      currentPlayer.main_position || currentPlayer.positions?.length,
      currentPlayer.club,
      currentPlayer.league,
      currentPlayer.height,
      currentPlayer.weight,
      currentPlayer.photo_url,
      currentPlayer.state,
      currentPlayer.lga,
      currentPlayer.foot,
      currentPlayer.goals > 0 || currentPlayer.assists > 0,
    ]
    const filledCount = fields.filter(Boolean).length
    const completionPart = (filledCount / fields.length) * 50

    // 2. Video Clips - worth up to 15 pts
    const videoPart = videos.length > 0 ? 15 : 0

    // 3. Career Timeline - worth up to 15 pts
    const timelinePart = (currentPlayer.match_history?.length || 0) > 0 ? 15 : 0

    // 4. Documents - worth up to 10 pts
    const docs = [
      currentPlayer.cv_url,
      currentPlayer.resume_url,
      currentPlayer.passport_url,
      currentPlayer.birth_certificate_url,
      currentPlayer.medical_report_url
    ]
    const hasDocs = docs.filter(Boolean).length > 0
    const docsPart = hasDocs ? 10 : 0

    // 5. Achievements - worth up to 10 pts
    const achievementPart = achievements.length > 0 ? 10 : 0

    return Math.round(completionPart + videoPart + timelinePart + docsPart + achievementPart)
  }, [currentPlayer, videos, achievements])

  const visibilityScore = useMemo(() => {
    if (!currentPlayer) return 0
    let score = 0
    if (currentPlayer.is_public !== false) score += 50
    if (currentPlayer.discoverable_public !== false) score += 50
    return score
  }, [currentPlayer])

  const matchHistoryCount = currentPlayer?.match_history?.length || 0
  const unreadNotifications = notifications.filter(n => !n.is_read).length
  const profileViews = notifications.filter(n => n.type === 'profile_view').length
  const scoutInterest = notifications.filter(n => n.type === 'profile_save' && n.actor_role === 'scout').length
  const clubInterest = notifications.filter(n => n.type === 'profile_save' && n.actor_role === 'club').length

  const stats = [
    { label: 'Profile Strength', value: `${profileStrength}%`, icon: Target, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'Visibility Score', value: `${visibilityScore}%`, icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Profile Views', value: profileViews, icon: Eye, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Scout Interest', value: scoutInterest, icon: Search, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Club Interest', value: clubInterest, icon: Building2, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Match History', value: matchHistoryCount, icon: Video, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
    { label: 'Unread Messages', value: unreadCount, icon: MessageSquare, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'Notifications', value: unreadNotifications, icon: Bell, color: 'text-red-500', bg: 'bg-red-500/10' },
  ]

  const recentActivity = notifications
    .filter(n => n.is_read !== undefined)
    .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
    .slice(0, 5)
    .map(n => {
      const typeMap: Record<string, { icon: any; color: string; bg: string; text: string }> = {
        profile_view: { icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10', text: n.message || 'Profile viewed' },
        profile_save: { icon: Bookmark, color: 'text-orange-400', bg: 'bg-orange-500/10', text: n.message || 'Profile saved by a scout' },
        message: { icon: MessageSquare, color: 'text-yellow-400', bg: 'bg-yellow-500/10', text: n.message || 'New message received' },
        trial_invitation: { icon: UserPlus, color: 'text-purple-400', bg: 'bg-purple-500/10', text: n.message || 'Trial invitation received' },
        clip_upload: { icon: Upload, color: 'text-green-400', bg: 'bg-green-500/10', text: n.message || 'Match clip uploaded' },
      }
      const info = typeMap[n.type] || { icon: Eye, color: 'text-gray-400', bg: 'bg-gray-500/10', text: n.message || n.type || 'Activity' }
      const diff = Date.now() - new Date(n.created_at || 0).getTime()
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(hours / 24)
      const time = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : 'Just now'
      return { ...info, time }
    })

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Welcome Banner */}
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-green-900/20 to-blue-900/20 p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">Welcome, {user?.name || 'Player'}</h2>
                  <p className="mt-2 text-gray-400">Manage your football career, showcase your skills, and connect with scouts and clubs.</p>
                </div>
                {currentPlayer && (
                  <button
                    onClick={handleShare}
                    className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-black uppercase text-xs transition border sm:ml-4 whitespace-nowrap ${
                      shareCopied
                        ? 'bg-[#7cff01]/10 border-[#7cff01]/20 text-[#7cff01]'
                        : 'bg-white/5 border-white/5 text-white hover:bg-white/10'
                    }`}
                  >
                    <Share2 size={16} />
                    {shareCopied ? 'Link Copied!' : 'Share Profile'}
                  </button>
                )}
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="stagger-card animate-fade-in-up bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm hover:border-green-500/20 hover:shadow-[0_0_20px_rgba(34,197,94,0.06)] transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">{item.label}</h3>
                    <div className={`w-8 h-8 rounded-xl ${item.bg} flex items-center justify-center`}>
                      <item.icon size={16} className={item.color} />
                    </div>
                  </div>
                  <p className="text-3xl font-black text-white">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-green-500" />
                  <h3 className="text-xl font-bold text-white">Recent Activity</h3>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Last 7 days</span>
              </div>
              <div className="space-y-4">
                {recentActivity.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No recent activity yet. Players viewed, clips uploaded, and messages will appear here.</p>
                ) : recentActivity.map((activity, i) => (
                  <div key={i} className="stagger-card animate-fade-in-up flex items-center gap-4 p-3 bg-[#0d1117] rounded-xl border border-gray-800 hover:border-green-500/20 transition-all duration-300">
                    <div className={`w-10 h-10 rounded-xl ${activity.bg} flex items-center justify-center flex-shrink-0`}>
                      <activity.icon size={18} className={activity.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{activity.text}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest">{activity.time}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-600 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { tab: 'profile' as TabType, label: 'Edit Profile', desc: 'Update your football details' },
                { tab: 'clips' as TabType, label: 'Manage Clips', desc: 'Upload match highlights' },
                { tab: 'timeline' as TabType, label: 'Career Timeline', desc: 'View your career history' },
              ].map((action) => (
                <button
                  key={action.tab}
                  onClick={() => setActiveTab(action.tab)}
                  className="stagger-card animate-fade-in-up text-left p-5 bg-[#161b22] border border-gray-800 rounded-2xl hover:border-green-500/30 hover:shadow-[0_0_20px_rgba(34,197,94,0.06)] transition-all duration-300 group"
                >
                  <span className="text-sm font-bold text-white group-hover:text-green-400 transition">{action.label}</span>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">{action.desc}</p>
                </button>
              ))}
              {currentPlayer && (
                <button
                  onClick={handleShare}
                  className={`stagger-card animate-fade-in-up text-left p-5 bg-[#161b22] border border-gray-800 rounded-2xl hover:border-green-500/30 hover:shadow-[0_0_20px_rgba(34,197,94,0.06)] transition-all duration-300 group flex items-center gap-2 ${
                    shareCopied
                      ? 'bg-[#7cff01]/10 border-[#7cff01]/20 text-[#7cff01]'
                      : ''
                  }`}
                >
                  <Share2 size={18} className="group-hover:text-green-400 transition" />
                  <div className="flex-1">
                    <span className="text-sm font-bold text-white group-hover:text-green-400 transition">{shareCopied ? 'Link Copied!' : 'Share Profile'}</span>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Copy link to your public profile</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        )
      case 'profile':
        return <PlayerDashboardProfile />
      case 'clips':
        return <PlayerMatchClips player={currentPlayer || undefined} />
      case 'statistics':
        return <PlayerStatistics player={currentPlayer || undefined} />
      case 'timeline':
        return <PlayerCareerTimeline player={currentPlayer || undefined} />
      case 'achievements':
        return <PlayerAchievements player={currentPlayer || undefined} />
      case 'messages':
        return <PlayerMessages />
      case 'trials':
        return <PlayerTrialInvitations />
      case 'documents':
        return <PlayerDocuments />
      case 'notifications':
        return <PlayerNotifications />
      case 'settings':
        return <PlayerSettings />
      case 'affiliates':
        return <AffiliateDashboard />
    }
  }

  return (
    <div className="min-h-screen bg-[#050910]">
      <header className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/scoutang-logo.svg" alt="Scouta" className="w-8 h-8" />
              <span className="text-lg font-bold text-white">Player Dashboard</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">{user?.email}</span>
              {currentPlayer && (
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-900/10 hover:bg-green-900/20 border border-green-900/30 rounded-xl text-green-400 transition text-xs font-black uppercase tracking-widest"
                >
                  <ExternalLink size={14} />
                  <span className="hidden sm:inline">Preview Profile</span>
                </button>
              )}
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-900/10 hover:bg-red-900/20 border border-red-900/30 rounded-xl text-red-400 transition text-xs font-black uppercase tracking-widest"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 flex gap-1 px-4 py-0 bg-gray-950 overflow-x-auto whitespace-nowrap hide-scrollbar">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm transition border-b-2 whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {renderTabContent()}
      </main>

      {showPreview && currentPlayer && (
        <div className="fixed inset-0 z-50 bg-[#050910] overflow-auto">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <button
              onClick={() => setShowPreview(false)}
              className="flex items-center gap-2 px-4 py-2 mb-4 bg-gray-900 border border-gray-700 rounded-xl text-gray-300 hover:text-white hover:border-green-500/30 transition text-sm font-bold"
            >
              ← Back to Dashboard
            </button>
          </div>
          <PublicPlayerProfile
            player={currentPlayer}
            onBack={() => setShowPreview(false)}
          />
        </div>
      )}
    </div>
  )
}

export default PlayerDashboard
